// ABOUTME: <sticky-footer> web component for the sticky PDP CTA footer (BL-STICKY-FOOTER).
// Shows the bar where live's shows it on production: on the first screen of a phone, where the gallery pushes
// the buy box below it, and wherever the buy box is off screen; hidden while the buy box or the page footer is
// >=20% in view. The buy box is the Add to Cart form (product-form-component) where the details sit under the
// gallery, and the whole product section (.product-information) where they sit beside it (from 990px), so the
// desktop first screen, which already shows the title, stars and price, stays clear as live's does. The CTA
// scrolls the Add to Cart into view; once the page's product is in the cart it reads Checkout and goes to
// checkout through the cart drawer's own Checkout link, and an add on the page brings the bar up at once, over
// the buy box, so a shopper who has added (every add is silent and the drawer stays shut, 47-R51, D176) has
// the next step on screen (D179).
// One exception is not this script's: in a viewport both short and narrow (a desktop page zoomed to 400%) the block's stylesheet does not display
// the bar at all, so what hide() and show() set here has nothing to show.
// Visibility is held as observer STATE (ratio flags), not threshold-crossing
// callbacks — the live component's crossing-order behavior breaks on pages short enough for both anchors to
// cross in one observer batch, leaving the bar parked over the footer. Hide/show mechanics are unchanged:
// the --hidden class plus an inline bottom offset of the page footer's height (0.6s ease).

import { StandardEvents } from '@shopify/events';

class StickyFooter extends HTMLElement {
  /** Anchors, listeners and observers are wired once; a re-insertion must not stack them. */
  #booted = false;

  // Children and the surrounding page are read here, not in the constructor: a constructor
  // runs before the element's children are parsed on the upgrade path, and the custom-elements
  // spec forbids the attribute writes setup() ends in (hide() adds a class and an inline style).
  connectedCallback() {
    if (this.#booted) return;
    this.#booted = true;

    this.backButton = this.querySelector('[id^="StickyFooterButton-"]');
    this.buttonLabel = this.backButton?.querySelector('span') || null;
    this.buttonArrow = this.backButton?.querySelector('svg') || null;
    this.buyNowText = this.buttonLabel?.textContent || '';
    this.footer = document.querySelector('footer');
    this.productSection = document.querySelector('.product-information');
    this.buyBox = this.productSection?.querySelector('product-form-component') || null;
    this.addToCart = this.buyBox?.querySelector('[name="add"]') || null;
    this.footerInView = false;
    this.sectionInView = false;
    this.buyBoxInView = false;
    this.inCart = false;
    this.pinned = false;
    this.cartEventSeen = false;
    if (this.backButton) {
      this.backButton.addEventListener('click', () => {
        this.handleBuyNow();
      });
    }
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    this.setup();
    // The cart as the page loads, unless the header's bag shows it empty; a cart event that got in first has the newer answer.
    if (!document.querySelector('.header-actions__cart-icon') || document.querySelector('.header-actions__cart-icon--has-cart')) {
      this.productInCart().then((inCart) => {
        if (!this.cartEventSeen) this.setInCart(inCart);
      });
    }
  }

  /**
   * Goes to checkout once the page's product is in the cart; until then brings the buy box's Add to Cart into view.
   * Checkout is reached by a click on the cart drawer's own Checkout link, the drawer shut, so whatever stops that
   * link (the order-limit app, on a cart over its limit) stops this tap too.
   */
  async handleBuyNow() {
    if (this.inCart || (await this.productInCart())) {
      const drawerCheckout = document.querySelector('#cart-drawer .jf-cart-drawer__checkout');
      if (drawerCheckout) {
        drawerCheckout.click();
      } else {
        window.location.assign(checkoutUrl());
      }
      return;
    }
    this.handleScrollToBuyBox();
  }

  /** Follows the cart: an add that leaves the page's product in it pins the bar, and the product leaving the cart unpins it. */
  onCartUpdate = (event) => {
    this.cartEventSeen = true;
    event.promise
      ?.then(async ({ detail }) => {
        const inCart = detail?.items ? this.holdsPageProduct(detail.items) : await this.productInCart();
        this.setInCart(inCart, event.action === 'add' && !detail?.didError);
      })
      // A cart change that failed is its sender's to report; the bar keeps what it last read.
      .catch(() => {});
  };

  /** Sets the CTA for the cart as read: the block's checkout label and a right-pointing arrow while the page's product is in it. After an add the buy box no longer hides the bar, until the product leaves the cart. */
  setInCart(inCart, added = false) {
    this.inCart = inCart;
    this.pinned = inCart && (this.pinned || added);
    const checkoutText = inCart && this.dataset.checkoutText;
    if (this.buttonLabel) this.buttonLabel.textContent = checkoutText || this.buyNowText;
    // The arrow is drawn pointing up, for the scroll up to Add to Cart; Checkout goes forward, so there it points right (D193).
    if (this.buttonArrow) this.buttonArrow.style.transform = checkoutText ? 'rotate(90deg)' : '';
    if (this.footer) this.update();
  }

  /** Whether a cart line is the page's product. */
  holdsPageProduct(items) {
    const productId = Number(this.buyBox?.dataset.productId);
    return Boolean(productId) && items.some((item) => item.product_id === productId);
  }

  /** Reads the cart for the page's product (adds and removals anywhere count). */
  async productInCart() {
    if (!this.buyBox?.dataset.productId) return false;
    try {
      const response = await fetch(`${window.Theme?.routes?.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      return this.holdsPageProduct(cart.items);
    } catch {
      return false;
    }
  }

  /** Brings the buy box's Add to Cart to the middle of the screen, whichever element scrolls the page. */
  handleScrollToBuyBox() {
    const target = this.addToCart || this.buyBox || this.productSection;
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  hide() {
    this.classList.add('jf-sticky-pdp-footer--hidden');
    this.style.bottom = `-${this.footer.clientHeight}px`;
  }

  show() {
    this.classList.remove('jf-sticky-pdp-footer--hidden');
    this.style.removeProperty('bottom');
  }

  /** Whether the product details sit beside the gallery (the two-column layout) rather than under it. */
  detailsBesideGallery() {
    const media = this.productSection?.querySelector('.product-information__media');
    const details = this.productSection?.querySelector('.product-details');
    if (!media || !details) return true;
    return details.getBoundingClientRect().top < media.getBoundingClientRect().bottom;
  }

  update() {
    const buyBoxInView = this.buyBox && !this.detailsBesideGallery() ? this.buyBoxInView : this.sectionInView;
    if (this.footerInView || (buyBoxInView && !this.pinned)) {
      this.hide();
    } else {
      this.show();
    }
  }

  setup() {
    if (!this.footer) return;

    // Anchors are flagged in-view at the live component's 20% visibility mark. The extra 0
    // threshold keeps the flags live on partial entries/exits of tall anchors.
    const thresholds = [0, 0.2];
    const watch = (anchor, flag) => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this[flag] = entry.intersectionRatio >= 0.2;
          });
          this.update();
        },
        { threshold: thresholds }
      );
      observer.observe(anchor);
    };

    // The first screen is decided from the page's geometry before any observer reports, so a bar
    // that belongs on the first screen stays where the page painted it instead of sliding out and back.
    this.footerInView = visibleRatio(this.footer) >= 0.2;
    if (this.productSection) this.sectionInView = visibleRatio(this.productSection) >= 0.2;
    if (this.buyBox) this.buyBoxInView = visibleRatio(this.buyBox) >= 0.2;
    this.update();

    watch(this.footer, 'footerInView');
    if (this.productSection) watch(this.productSection, 'sectionInView');
    if (this.buyBox) watch(this.buyBox, 'buyBoxInView');
    // A resize can move the details beside or under the gallery without any anchor crossing 20%.
    // iOS fires resize as Safari's toolbar collapses mid-scroll, so updates are coalesced per frame.
    let resizeQueued = false;
    window.addEventListener('resize', () => {
      if (resizeQueued) return;
      resizeQueued = true;
      requestAnimationFrame(() => {
        resizeQueued = false;
        this.update();
      });
    });
  }
}

/** Checkout under the market's root, for a page whose drawer has no Checkout link: Theme.routes has no checkout route, so it comes off the cart route ('/cart', '/en-ca/cart'). */
function checkoutUrl() {
  return Theme.routes.cart_url.replace(/cart$/, 'checkout');
}

/** The share of an element's height inside the viewport, the ratio IntersectionObserver reports for full-width anchors. */
function visibleRatio(element) {
  const rect = element.getBoundingClientRect();
  if (!rect.height) return 0;
  const visible = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
  return Math.max(0, visible) / rect.height;
}

if (!customElements.get('sticky-footer')) {
  customElements.define('sticky-footer', StickyFooter);
}
